import { motion } from 'framer-motion';
import {
  Calculator,
  CheckCircle,
  IndianRupee,
  Info,
  Percent,
  TrendingDown,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import RadioGroup from '@ui/RadioGroup';
import ResultCard from '@ui/ResultCard';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  formatIndianNumber,
  formatIndianNumberShort,
} from '@utils/numberFormatter';

export const TaxCalculator = () => {
  // Tax Calculator State
  const [annualIncome, setAnnualIncome] = useState<number>(1200000);
  const [taxRegime, setTaxRegime] = useState<string>('new'); // Default to new regime as per current rules
  const [age, setAge] = useState<string>('below60');
  const [standardDeduction, setStandardDeduction] = useState<number>(50000);
  const [section80C, setSection80C] = useState<number>(150000);
  const [section80CCD1B, setSection80CCD1B] = useState<number>(50000);
  const [section80D, setSection80D] = useState<number>(25000);
  const [homeLoanInterest, setHomeLoanInterest] = useState<number>(200000);
  const [section80E, setSection80E] = useState<number>(0);
  const [section80G, setSection80G] = useState<number>(0);
  const [section80TTA, setSection80TTA] = useState<number>(10000);
  const [hra, setHRA] = useState<number>(0);
  const [lta, setLTA] = useState<number>(0);
  const [otherDeductions, setOtherDeductions] = useState<number>(0);
  const [results, setResults] = useState({
    taxableIncome: 0,
    taxBeforeDeductions: 0,
    totalDeductions: 0,
    taxAfterDeductions: 0,
    taxSaved: 0,
    takeHomeIncome: 0,
  });

  // Tax slabs for different regimes and age groups (FY 2025-26 - Budget 2025)
  const getTaxSlabs = (regime: string, ageGroup: string) => {
    if (regime === 'new') {
      // New tax regime - Budget 2025 updated slabs (same for all age groups)
      return [
        { min: 0, max: 400000, rate: 0 }, // Up to ₹4L - Nil
        { min: 400000, max: 800000, rate: 5 }, // ₹4L-₹8L - 5%
        { min: 800000, max: 1200000, rate: 10 }, // ₹8L-₹12L - 10%
        { min: 1200000, max: 1600000, rate: 15 }, // ₹12L-₹16L - 15%
        { min: 1600000, max: 2000000, rate: 20 }, // ₹16L-₹20L - 20%
        { min: 2000000, max: 2400000, rate: 25 }, // ₹20L-₹24L - 25%
        { min: 2400000, max: Infinity, rate: 30 }, // Above ₹24L - 30%
      ];
    }

    // Old tax regime with age-based exemptions
    const slabs = {
      below60: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 },
      ],
      senior: [
        { min: 0, max: 300000, rate: 0 },
        { min: 300000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 },
      ],
      superSenior: [
        { min: 0, max: 500000, rate: 0 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 },
      ],
    };
    return slabs[ageGroup as keyof typeof slabs] || slabs.below60;
  };

  // Calculate tax based on slabs
  const calculateTax = useCallback(
    (income: number, regime: string, ageGroup: string) => {
      const slabs = getTaxSlabs(regime, ageGroup);
      let tax = 0;

      for (const slab of slabs) {
        if (income > slab.min) {
          const taxableInThisSlab = Math.min(
            income - slab.min,
            slab.max - slab.min
          );
          tax += (taxableInThisSlab * slab.rate) / 100;
        }
      }

      // Add 4% cess on total tax
      return tax * 1.04;
    },
    []
  );

  // Calculate tax with rebate under Section 87A (Budget 2025 updated)
  const calculateTaxWithRebate = useCallback(
    (income: number, regime: string, ageGroup: string) => {
      const baseTax = calculateTax(income, regime, ageGroup);

      // Apply rebate under Section 87A - Budget 2025 updates
      if (regime === 'new') {
        // New regime: Rebate increased to ₹60,000 - effectively no tax up to ₹12L
        if (income <= 1200000) {
          return 0; // Full rebate for income up to ₹12L in new regime
        } else {
          // Apply maximum rebate of ₹60,000 for higher incomes
          return Math.max(0, baseTax - 60000);
        }
      } else if (regime === 'old' && income <= 500000) {
        return 0; // Full rebate for income up to ₹5L in old regime (unchanged)
      }

      return baseTax;
    },
    [calculateTax]
  );

  // Calculate tax savings
  const calculateTaxSavings = useCallback(() => {
    let totalDeductions = 0;

    if (taxRegime === 'new') {
      // New regime allows only standard deduction and employer NPS contribution
      totalDeductions = standardDeduction;
    } else {
      // Old regime allows all deductions
      totalDeductions =
        standardDeduction +
        section80C +
        section80CCD1B +
        section80D +
        homeLoanInterest +
        section80E +
        section80G +
        section80TTA +
        hra +
        lta +
        otherDeductions;
    }

    const taxableIncome = Math.max(0, annualIncome - totalDeductions);

    const taxBeforeDeductions = calculateTaxWithRebate(
      annualIncome,
      taxRegime,
      age
    );
    const taxAfterDeductions = calculateTaxWithRebate(
      taxableIncome,
      taxRegime,
      age
    );
    const taxSaved = taxBeforeDeductions - taxAfterDeductions;
    const takeHomeIncome = annualIncome - taxAfterDeductions;

    setResults({
      taxableIncome,
      taxBeforeDeductions,
      totalDeductions,
      taxAfterDeductions,
      taxSaved,
      takeHomeIncome,
    });
  }, [
    annualIncome,
    taxRegime,
    age,
    standardDeduction,
    section80C,
    section80CCD1B,
    section80D,
    homeLoanInterest,
    section80E,
    section80G,
    section80TTA,
    hra,
    lta,
    otherDeductions,
    calculateTaxWithRebate,
  ]);

  // Recalculate when inputs change
  useEffect(() => {
    calculateTaxSavings();
  }, [calculateTaxSavings]);

  // Tax saving investments
  const taxSavingOptions = [
    {
      name: 'ELSS mutual funds',
      limit: '₹1.5L',
      returns: '12-15%',
      lockIn: '3 years',
    },
    { name: 'PPF', limit: '₹1.5L', returns: '7-8%', lockIn: '15 years' },
    { name: 'ULIP', limit: '₹1.5L', returns: '8-12%', lockIn: '5 years' },
    { name: 'NSC', limit: '₹1.5L', returns: '6-7%', lockIn: '5 years' },
    {
      name: 'Tax saver FD',
      limit: '₹1.5L',
      returns: '5-6%',
      lockIn: '5 years',
    },
    {
      name: 'Life insurance',
      limit: '₹1.5L',
      returns: 'Protection',
      lockIn: 'Policy term',
    },
  ];

  // Tax regime options (Budget 2025 updated)
  const taxRegimeOptions = [
    {
      value: 'new',
      label: 'New tax regime (Default)',
      description: 'No tax up to ₹12L, limited deductions (Budget 2025)',
    },
    {
      value: 'old',
      label: 'Old tax regime',
      description: 'Full deductions available, rebate up to ₹5L',
    },
  ];

  // Age group options (only for old regime)
  const ageOptions = [
    {
      value: 'below60',
      label: 'Below 60 years',
      description: 'Basic exemption: ₹2.5L',
    },
    {
      value: 'senior',
      label: '60-80 years (Senior)',
      description: 'Basic exemption: ₹3L',
    },
    {
      value: 'superSenior',
      label: 'Above 80 years (Super senior)',
      description: 'Basic exemption: ₹5L',
    },
  ];

  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  return (
    <ToolLayout
      title={`Tax calculator FY ${currentYear}-${nextYear} | ${CONTACT_INFO.company.name}`}
      description="Updated tax calculator with Budget 2025 changes - No tax up to ₹12L in new regime! Calculate income tax liability with latest slabs and rebate limits."
      canonicalUrl="https://niveshright.com/tools/tax-calculator"
      keywords={`tax calculator ${currentYear}, budget ${currentYear} tax calculator, income tax calculator FY ${currentYear}-${nextYear}, new tax regime 2025, 12 lakh tax free`}
      heroTitle="Tax calculator (Budget 2025 updated)"
      heroIcon={<Percent size={32} style={{ color: COLORS.sage }} />}
      heroStyle="gradient"
      showBreadcrumb={true}
      backgroundColor={COLORS.mist}
      cta={
        <WhatsAppCTA
          title={`Discuss how you can save taxes`}
          description={
            taxRegime === 'new' ? (
              <>
                Your tax liability is{' '}
                <LocalizedCurrency value={results.taxAfterDeductions} />.
              </>
            ) : (
              <>
                Save <LocalizedCurrency value={results.taxSaved} /> in taxes.
              </>
            )
          }
          buttonText={
            taxRegime === 'new'
              ? 'Reach out to us'
              : 'Discuss how you can save under 80C in old regime'
          }
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={
            taxRegime === 'new'
              ? `Hi ${CONTACT_INFO.contact_person.short_name}! I used your tax calculator (Budget 2025) with these details:

Annual income: ₹${annualIncome.toLocaleString()}
Tax regime: New regime
Total deductions: ₹${results.totalDeductions.toLocaleString()}

TAX CALCULATION:
• Taxable income: ₹${results.taxableIncome.toLocaleString()}
• Tax before deductions: ₹${Math.round(results.taxBeforeDeductions).toLocaleString()}
• Final tax liability: ₹${Math.round(results.taxAfterDeductions).toLocaleString()}
• Take-home income: ₹${results.takeHomeIncome.toLocaleString()}

${results.taxAfterDeductions > 0 ? `I'd like advice on optimizing my tax liability and financial planning.` : `Great! No tax liability. I'd still like financial planning advice.`} Can you help?`
              : `Hi ${CONTACT_INFO.contact_person.short_name}! I used your tax calculator (Budget 2025) with these details:

Annual income: ₹${annualIncome.toLocaleString()}
Tax regime: Old regime
Total deductions: ₹${results.totalDeductions.toLocaleString()}

TAX CALCULATION:
• Taxable income: ₹${results.taxableIncome.toLocaleString()}
• Tax before deductions: ₹${Math.round(results.taxBeforeDeductions).toLocaleString()}
• Final tax liability: ₹${Math.round(results.taxAfterDeductions).toLocaleString()}
• Tax saved: ₹${Math.round(results.taxSaved).toLocaleString()}
• Take-home income: ₹${results.takeHomeIncome.toLocaleString()}

${results.taxSaved > 0 ? `I can save ₹${Math.round(results.taxSaved).toLocaleString()} in taxes! Please suggest good tax-saving investments.` : `I'd like advice on tax-saving investments and financial planning.`} Can you help?`
          }
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.accent}
        />
      }
    >
      {/* Budget 2025 Update Notice */}
      <div
        className="border rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
        style={{
          background: `linear-gradient(135deg, ${COLORS.secondary} 0%, ${COLORS.smoke} 100%)`,
          color: COLORS.gray100,
        }}
      >
        <div className="flex items-center mb-3 sm:mb-4">
          <div
            className="rounded-full p-1.5 sm:p-2 mr-3 sm:mr-4"
            style={{ backgroundColor: COLORS.mist }}
          >
            <CheckCircle
              size={16}
              className="sm:w-5 sm:h-5"
              style={{ color: COLORS.gray100 }}
            />
          </div>
          <h3 className="text-base sm:text-lg font-semibold">
            Budget 2025 updates applied
          </h3>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm"
          style={{ color: COLORS.gray200 }}
        >
          <div
            className="border rounded-lg p-2 sm:p-3"
            style={{ borderColor: COLORS.gray400 }}
          >
            <p className="font-medium mb-0.5 sm:mb-1">Zero tax up to ₹12L</p>
            <p style={{ color: COLORS.gray300 }}>
              New regime rebate increased to ₹60,000
            </p>
          </div>
          <div
            className="border rounded-lg p-2 sm:p-3"
            style={{ borderColor: COLORS.gray400 }}
          >
            <p className="font-medium mb-0.5 sm:mb-1">Higher basic exemption</p>
            <p style={{ color: COLORS.gray300 }}>
              ₹4L basic exemption (vs ₹2.5L in old)
            </p>
          </div>
          <div
            className="border rounded-lg p-2 sm:p-3"
            style={{ borderColor: COLORS.gray400 }}
          >
            <p className="font-medium mb-0.5 sm:mb-1">Updated tax slabs</p>
            <p style={{ color: COLORS.gray300 }}>
              New progressive rates for FY 2025-26
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100">
          <h2
            className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8"
            style={{ color: COLORS.primary }}
          >
            Income & tax details
          </h2>

          {/* Tax Regime Selection */}
          <RadioGroup
            label="Tax regime"
            options={taxRegimeOptions}
            value={taxRegime}
            onChange={setTaxRegime}
            className="mb-6 sm:mb-8"
          />

          {/* Annual Income */}
          <NumberInputWithSlider
            label="Annual income"
            value={annualIncome}
            onChange={value => setAnnualIncome(value || 0)}
            min={0}
            max={100000000}
            step={10000}
            prefix="₹"
            showTooltip={true}
            minLabel="₹0"
            maxLabel={`₹${formatIndianNumberShort(100000000, false)}`}
            className="mb-6 sm:mb-8"
          />

          {/* Age Group - Only show for old regime */}
          {taxRegime === 'old' && (
            <RadioGroup
              label="Age group"
              options={ageOptions}
              value={age}
              onChange={setAge}
              className="mb-6 sm:mb-8"
            />
          )}

          {/* Standard Deduction - Available in both regimes */}
          <NumberInputWithSlider
            label="Standard deduction (salaried/pensioners)"
            value={standardDeduction}
            onChange={value => setStandardDeduction(value || 50000)}
            min={0}
            max={50000}
            step={1000}
            prefix="₹"
            showTooltip={true}
            minLabel="₹0"
            maxLabel={`₹${formatIndianNumberShort(50000, false)}`}
            className="mb-6"
          />
          <p className="text-xs text-gray-500 mb-6">
            Fixed ₹50,000 for salaried individuals and pensioners
          </p>

          {/* Comprehensive Deduction Inputs - Only show for old regime */}
          {taxRegime === 'old' && (
            <>
              <div className="mt-8 mb-6">
                <hr className="border-gray-200 mb-6" />
                <h3
                  className="text-sm font-bold uppercase tracking-wide mb-2"
                  style={{ color: COLORS.smoke }}
                >
                  Chapter VI-A deductions
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Major tax-saving investments and expenses
                </p>
              </div>

              <NumberInputWithSlider
                label="Section 80C - Tax saving investments (max ₹1.5L)"
                value={section80C}
                onChange={value => setSection80C(Math.min(150000, value || 0))}
                min={0}
                max={150000}
                step={1000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹1.5L`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                PPF, EPF, ELSS, NSC, ULIP, Life insurance premium, Home loan
                principal
              </p>

              <NumberInputWithSlider
                label="Section 80CCD(1B) - Additional NPS (max ₹50K)"
                value={section80CCD1B}
                onChange={value =>
                  setSection80CCD1B(Math.min(50000, value || 0))
                }
                min={0}
                max={50000}
                step={1000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(50000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Additional contribution to National Pension System (NPS)
              </p>

              <NumberInputWithSlider
                label="Section 80D - Health insurance premium"
                value={section80D}
                onChange={value => setSection80D(Math.min(100000, value || 0))}
                min={0}
                max={100000}
                step={1000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(100000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Self, family & parents (limits vary by age)
              </p>

              <div className="mt-8 mb-6">
                <hr className="border-gray-200 mb-6" />
                <h3
                  className="text-sm font-bold uppercase tracking-wide mb-2"
                  style={{ color: COLORS.smoke }}
                >
                  Property & loan deductions
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Housing and education loan benefits
                </p>
              </div>

              <NumberInputWithSlider
                label="Home loan interest - Section 24(b) (max ₹2L)"
                value={homeLoanInterest}
                onChange={value =>
                  setHomeLoanInterest(Math.min(200000, value || 0))
                }
                min={0}
                max={200000}
                step={5000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(200000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Interest on housing loan for self-occupied property
              </p>

              <NumberInputWithSlider
                label="Section 80E - Education loan interest"
                value={section80E}
                onChange={value => setSection80E(value || 0)}
                min={0}
                max={500000}
                step={5000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(500000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Interest on education loan (no upper limit)
              </p>

              <div className="mt-8 mb-6">
                <hr className="border-gray-200 mb-6" />
                <h3
                  className="text-sm font-bold uppercase tracking-wide mb-2"
                  style={{ color: COLORS.smoke }}
                >
                  Allowances & other deductions
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Salary allowances and miscellaneous deductions
                </p>
              </div>

              <NumberInputWithSlider
                label="House Rent Allowance (HRA)"
                value={hra}
                onChange={value => setHRA(value || 0)}
                min={0}
                max={1000000}
                step={5000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(1000000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Exemption as per HRA rules
              </p>

              <NumberInputWithSlider
                label="Leave Travel Allowance (LTA)"
                value={lta}
                onChange={value => setLTA(value || 0)}
                min={0}
                max={100000}
                step={1000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(100000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Travel allowance exemption
              </p>

              <NumberInputWithSlider
                label="Section 80G - Donations"
                value={section80G}
                onChange={value => setSection80G(value || 0)}
                min={0}
                max={200000}
                step={1000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(200000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Donations to specified funds and charitable institutions
              </p>

              <NumberInputWithSlider
                label="Section 80TTA - Savings account interest (max ₹10K)"
                value={section80TTA}
                onChange={value => setSection80TTA(Math.min(10000, value || 0))}
                min={0}
                max={10000}
                step={500}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(10000, false)}`}
                className="mb-4"
              />
              <p className="text-xs text-gray-500 mb-6">
                Interest on savings account
              </p>

              <NumberInputWithSlider
                label="Other deductions"
                value={otherDeductions}
                onChange={value => setOtherDeductions(value || 0)}
                min={0}
                max={500000}
                step={1000}
                prefix="₹"
                showTooltip={true}
                minLabel="₹0"
                maxLabel={`₹${formatIndianNumberShort(500000, false)}`}
                className="mb-6 sm:mb-8"
              />
              <p className="text-xs text-gray-500 mb-8">
                80DD, 80DDB, 80EEA, Professional tax, etc.
              </p>
            </>
          )}

          {/* Regime-specific Guidance */}
          <div
            className={`${taxRegime === 'new' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} border rounded-xl p-4`}
          >
            <div className="flex items-start">
              <Info
                size={20}
                className={`${taxRegime === 'new' ? 'text-blue-600' : 'text-green-600'} mt-0.5 mr-3 flex-shrink-0`}
              />
              <div
                className={`text-sm ${taxRegime === 'new' ? 'text-blue-800' : 'text-green-800'}`}
              >
                <p className="font-medium mb-2">
                  {taxRegime === 'new'
                    ? 'New regime (Default) - FY 2025-26 (Budget 2025):'
                    : 'Old regime - FY 2025-26:'}
                </p>
                {taxRegime === 'new' ? (
                  <ul className="space-y-1 text-xs">
                    <li>
                      • <strong>No tax up to ₹12,00,000</strong> (Budget 2025
                      update)
                    </li>
                    <li>
                      • Higher basic exemption: ₹4,00,000 (vs ₹2,50,000 in old)
                    </li>
                    <li>• Rebate increased to ₹60,000 from ₹25,000</li>
                    <li>
                      • Only standard deduction (₹50,000) and employer NPS
                      allowed
                    </li>
                    <li>
                      • Best for: Income up to ₹12L, minimal investments,
                      simplified filing
                    </li>
                  </ul>
                ) : (
                  <ul className="space-y-1 text-xs">
                    <li>
                      • Full deductions available: 80C, 80D, HRA, home loan
                      interest
                    </li>
                    <li>
                      • Rebate available up to ₹5,00,000 taxable income
                      (unchanged)
                    </li>
                    <li>
                      • Best for: High investments, home loans, significant HRA
                    </li>
                    <li>• Calculate both regimes to choose the optimal one!</li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Tax Summary Cards */}
          <div className="grid grid-cols-1 gap-6">
            <ResultCard
              icon={<TrendingDown size={24} />}
              title={taxRegime === 'new' ? 'Tax payable' : 'Tax saved'}
              value={
                <LocalizedCurrency
                  value={
                    taxRegime === 'new'
                      ? results.taxAfterDeductions
                      : results.taxSaved
                  }
                />
              }
              subtitle={
                taxRegime === 'new'
                  ? 'Lower tax with new regime'
                  : 'Annual tax savings'
              }
              color={COLORS.sage}
            />

            <ResultCard
              icon={<Calculator size={24} />}
              title="Tax Liability"
              value={<LocalizedCurrency value={results.taxAfterDeductions} />}
              subtitle={
                taxRegime === 'new'
                  ? 'Final tax amount'
                  : 'After all deductions'
              }
              color={COLORS.accent}
            />

            <ResultCard
              icon={<IndianRupee size={24} />}
              title="Take-home income"
              value={<LocalizedCurrency value={results.takeHomeIncome} />}
              subtitle="Annual income after tax"
              color={COLORS.gold}
            />
          </div>

          {/* Tax Breakdown */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <h3
              className="text-lg font-semibold mb-6"
              style={{ color: COLORS.primary }}
            >
              Tax calculation breakdown
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <span
                  className="font-medium"
                  style={{ color: COLORS.charcoal }}
                >
                  Gross income
                </span>
                <span className="font-bold" style={{ color: COLORS.charcoal }}>
                  <LocalizedCurrency value={annualIncome} />
                </span>
              </div>

              {taxRegime === 'old' && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <span
                    className="font-medium"
                    style={{ color: COLORS.charcoal }}
                  >
                    Total deductions
                  </span>
                  <span className="font-bold text-green-600">
                    -<LocalizedCurrency value={results.totalDeductions} />
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <span
                  className="font-medium"
                  style={{ color: COLORS.charcoal }}
                >
                  Taxable income
                </span>
                <span className="font-bold" style={{ color: COLORS.charcoal }}>
                  <LocalizedCurrency value={results.taxableIncome} />
                </span>
              </div>

              {taxRegime === 'old' && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <span
                    className="font-medium"
                    style={{ color: COLORS.charcoal }}
                  >
                    Tax before deductions
                  </span>
                  <span className="font-bold text-red-600">
                    <LocalizedCurrency value={results.taxBeforeDeductions} />
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <span
                  className="font-medium"
                  style={{ color: COLORS.charcoal }}
                >
                  Final tax liability
                </span>
                <span className="font-bold" style={{ color: COLORS.charcoal }}>
                  <LocalizedCurrency value={results.taxAfterDeductions} />
                </span>
              </div>
            </div>
          </motion.div>

          {/* Slab-wise Tax Breakdown */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.1 }}
          >
            <h3
              className="text-lg font-semibold mb-6"
              style={{ color: COLORS.primary }}
            >
              Slab-wise tax calculation (
              {taxRegime === 'new' ? 'new regime - Budget 2025' : 'old regime'})
            </h3>

            {(() => {
              const slabs = getTaxSlabs(taxRegime, age);
              const slabBreakdown = [];
              let remainingIncome = results.taxableIncome;
              let totalTax = 0;

              // Show all slabs, including those with zero tax for completeness
              for (const slab of slabs) {
                const taxableInThisSlab =
                  remainingIncome > slab.min
                    ? Math.min(remainingIncome - slab.min, slab.max - slab.min)
                    : 0;
                const taxInThisSlab = (taxableInThisSlab * slab.rate) / 100;
                totalTax += taxInThisSlab;

                slabBreakdown.push({
                  range:
                    slab.max === Infinity
                      ? `Above ₹${(slab.min / 100000).toFixed(1)}L`
                      : `₹${(slab.min / 100000).toFixed(1)}L - ₹${(slab.max / 100000).toFixed(1)}L`,
                  rate: slab.rate,
                  taxableAmount: taxableInThisSlab,
                  taxAmount: taxInThisSlab,
                  hasIncome: taxableInThisSlab > 0,
                });
              }

              const totalTaxWithCess = totalTax * 1.04;
              const finalTaxAfterRebate = calculateTaxWithRebate(
                results.taxableIncome,
                taxRegime,
                age
              );
              const rebateAmount = totalTaxWithCess - finalTaxAfterRebate;
              const effectiveRate =
                results.taxableIncome > 0
                  ? (finalTaxAfterRebate / results.taxableIncome) * 100
                  : 0;

              return (
                <div className="space-y-4">
                  {/* Slab-wise breakdown table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th
                            className="text-left py-3 px-2 font-semibold"
                            style={{ color: COLORS.charcoal }}
                          >
                            Income slab
                          </th>
                          <th
                            className="text-center py-3 px-2 font-semibold"
                            style={{ color: COLORS.charcoal }}
                          >
                            Tax rate
                          </th>
                          <th
                            className="text-right py-3 px-2 font-semibold"
                            style={{ color: COLORS.charcoal }}
                          >
                            Taxable amount
                          </th>
                          <th
                            className="text-right py-3 px-2 font-semibold"
                            style={{ color: COLORS.charcoal }}
                          >
                            Tax amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {slabBreakdown.map((slab, index) => (
                          <tr
                            key={index}
                            className={`border-b border-gray-100 ${!slab.hasIncome ? 'opacity-60' : ''}`}
                          >
                            <td className="py-3 px-2 font-medium">
                              {slab.range}
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  slab.rate === 0
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {slab.rate}%
                              </span>
                            </td>
                            <td className="py-3 px-2 text-right font-medium">
                              {!slab.hasIncome && slab.taxableAmount === 0 ? (
                                <span className="text-sm text-gray-400">
                                  N/A
                                </span>
                              ) : (
                                <LocalizedCurrency value={slab.taxableAmount} />
                              )}
                            </td>
                            <td
                              className="py-3 px-2 text-right font-bold"
                              style={{ color: COLORS.accent }}
                            >
                              {!slab.hasIncome && slab.taxAmount === 0 ? (
                                <span className="text-sm text-gray-400">
                                  N/A
                                </span>
                              ) : (
                                <LocalizedCurrency value={slab.taxAmount} />
                              )}
                            </td>
                          </tr>
                        ))}

                        {/* Subtotal */}
                        <tr className="border-t-2 border-gray-300 bg-gray-50">
                          <td className="py-3 px-2 font-bold" colSpan={3}>
                            Subtotal (before Cess)
                          </td>
                          <td
                            className="py-3 px-2 text-right font-bold"
                            style={{ color: COLORS.accent }}
                          >
                            <LocalizedCurrency value={totalTax} />
                          </td>
                        </tr>

                        {/* Health & Education Cess */}
                        <tr className="bg-gray-50">
                          <td className="py-3 px-2 font-medium" colSpan={3}>
                            Health & Education Cess (4%)
                          </td>
                          <td
                            className="py-3 px-2 text-right font-medium"
                            style={{ color: COLORS.accent }}
                          >
                            <LocalizedCurrency value={totalTax * 0.04} />
                          </td>
                        </tr>

                        {/* Total before rebate */}
                        <tr className="bg-gray-50">
                          <td className="py-3 px-2 font-bold" colSpan={3}>
                            Total tax (before Rebate)
                          </td>
                          <td
                            className="py-3 px-2 text-right font-bold"
                            style={{ color: COLORS.accent }}
                          >
                            <LocalizedCurrency value={totalTaxWithCess} />
                          </td>
                        </tr>

                        {/* Rebate */}
                        {rebateAmount > 0 && (
                          <tr className="bg-green-50">
                            <td className="py-3 px-2 font-medium" colSpan={3}>
                              Rebate u/s 87A{' '}
                              {taxRegime === 'new'
                                ? '(Up to ₹60,000)'
                                : '(Up to ₹12,500)'}
                            </td>
                            <td className="py-3 px-2 text-right font-medium text-green-600">
                              -<LocalizedCurrency value={rebateAmount} />
                            </td>
                          </tr>
                        )}

                        {/* Final tax */}
                        <tr className="border-t-2 border-gray-400 bg-blue-50">
                          <td
                            className="py-4 px-2 font-bold text-lg"
                            colSpan={3}
                          >
                            Final tax liability
                          </td>
                          <td
                            className="py-4 px-2 text-right font-bold text-lg"
                            style={{ color: COLORS.accent }}
                          >
                            <LocalizedCurrency value={finalTaxAfterRebate} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Summary metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">
                        Effective tax rate
                      </p>
                      <p
                        className="text-xl font-bold"
                        style={{ color: COLORS.accent }}
                      >
                        {effectiveRate.toFixed(2)}%
                      </p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">
                        Average tax rate
                      </p>
                      <p
                        className="text-xl font-bold"
                        style={{ color: COLORS.sage }}
                      >
                        {annualIncome > 0
                          ? (
                              (finalTaxAfterRebate / annualIncome) *
                              100
                            ).toFixed(2)
                          : 0}
                        %
                      </p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">
                        Marginal tax rate
                      </p>
                      <p
                        className="text-xl font-bold"
                        style={{ color: COLORS.gold }}
                      >
                        {slabBreakdown.length > 0
                          ? slabBreakdown[slabBreakdown.length - 1].rate
                          : 0}
                        %
                      </p>
                    </div>
                  </div>

                  {/* Regime comparison note */}
                  {taxRegime === 'new' && results.taxableIncome <= 1200000 && (
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mt-4">
                      <div className="flex items-center">
                        <CheckCircle size={20} className="text-gray-600 mr-3" />
                        <div className="text-sm text-gray-800">
                          <p className="font-semibold">
                            Budget 2025 benefit applied
                          </p>
                          <p>
                            Your income qualifies for zero tax under the new
                            regime with the increased rebate limit.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </motion.div>
        </div>
      </div>

      {/* Tax Slab Comparison */}
      <motion.section
        className="py-16"
        style={{ backgroundColor: COLORS.mist }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.0 }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2
              className="text-3xl font-bold mb-4"
              style={{ color: COLORS.primary }}
            >
              Tax slab rates comparison (FY 2025-26)
            </h2>
            <p className="text-xl" style={{ color: COLORS.smoke }}>
              Compare tax rates between old and new regime
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* New Regime Slabs */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 rounded-full p-2 mr-3">
                  <CheckCircle size={20} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  New tax regime (Budget 2025)
                </h3>
              </div>

              <div className="space-y-3">
                {getTaxSlabs('new', 'below60').map((slab, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="font-medium text-gray-800">
                      {slab.max === Infinity
                        ? `Above ₹${(slab.min / 100000).toFixed(1)}L`
                        : `₹${(slab.min / 100000).toFixed(1)}L - ₹${(slab.max / 100000).toFixed(1)}L`}
                    </span>
                    <span className="text-sm font-medium text-gray-700">
                      {slab.rate}%
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  Budget 2025 benefits:
                </p>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• No tax up to ₹12,00,000 (with rebate)</li>
                  <li>• Higher basic exemption: ₹4,00,000</li>
                  <li>• Simplified tax structure</li>
                </ul>
              </div>
            </div>

            {/* Old Regime Slabs */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="bg-green-100 rounded-full p-2 mr-3">
                  <Calculator size={20} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Old tax regime
                </h3>
              </div>

              <div className="space-y-3">
                {getTaxSlabs('old', age).map((slab, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="font-medium text-gray-800">
                      {slab.max === Infinity
                        ? `Above ₹${(slab.min / 100000).toFixed(1)}L`
                        : `₹${(slab.min / 100000).toFixed(1)}L - ₹${(slab.max / 100000).toFixed(1)}L`}
                    </span>
                    <span className="text-sm font-medium text-gray-700">
                      {slab.rate}%
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  Old regime benefits:
                </p>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Full deductions available (80C, 80D, HRA)</li>
                  <li>• Age-based exemptions</li>
                  <li>• Rebate up to ₹5,00,000</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Quick comparison */}
          <div className="mt-6 sm:mt-12 bg-white rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100">
            <h3
              className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center"
              style={{ color: COLORS.primary }}
            >
              Quick tax comparison (sample incomes)
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-bold text-gray-700 text-xs sm:text-sm">
                      Annual income
                    </th>
                    <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-bold text-gray-700 text-xs sm:text-sm">
                      New regime tax
                    </th>
                    <th className="text-center py-2 sm:py-3 px-2 sm:px-4 font-bold text-gray-700 text-xs sm:text-sm">
                      Old regime tax*
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      income: 500000,
                      label: formatIndianNumber(500000),
                    },
                    {
                      income: 800000,
                      label: formatIndianNumber(800000),
                    },
                    {
                      income: 1200000,
                      label: formatIndianNumber(1200000),
                    },
                    {
                      income: 1500000,
                      label: formatIndianNumber(1500000),
                    },
                    {
                      income: 2000000,
                      label: formatIndianNumber(2000000),
                    },
                  ].map((sample, index) => {
                    const newRegimeTax = calculateTaxWithRebate(
                      sample.income,
                      'new',
                      'below60'
                    );
                    const oldRegimeTax = calculateTaxWithRebate(
                      sample.income,
                      'old',
                      'below60'
                    );

                    return (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm">
                          {sample.label}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-gray-700 text-xs sm:text-sm">
                          <LocalizedCurrency value={newRegimeTax} />
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-gray-700 text-xs sm:text-sm">
                          <LocalizedCurrency value={oldRegimeTax} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-500 mt-3 sm:mt-4 text-center">
              *Old regime tax calculated without deductions for comparison.
              Actual tax may be lower with deductions.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Comprehensive Deduction Comparison */}
      <motion.section
        className="py-8 sm:py-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-12">
            <h2
              className="text-xl sm:text-3xl font-bold mb-3 sm:mb-4"
              style={{ color: COLORS.primary }}
            >
              Complete deduction comparison: Old vs new regime (Budget 2025)
            </h2>
            <p className="text-base sm:text-xl" style={{ color: COLORS.smoke }}>
              Updated with Budget 2025 changes - No tax up to ₹12L in new
              regime!
            </p>
          </div>

          <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-2 sm:py-4 px-2 sm:px-4 font-bold text-gray-700 text-xs sm:text-sm">
                    Deduction/exemption
                  </th>
                  <th className="text-center py-2 sm:py-4 px-2 sm:px-4 font-bold text-gray-700 text-xs sm:text-sm">
                    Old tax regime
                  </th>
                  <th className="text-center py-2 sm:py-4 px-2 sm:px-4 font-bold text-gray-700 text-xs sm:text-sm">
                    New tax regime
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  {
                    category: 'Basic deductions',
                    items: [
                      {
                        name: 'Standard deduction',
                        old: '₹50,000',
                        new: '₹50,000',
                        available: true,
                      },
                      {
                        name: 'Basic exemption',
                        old: '₹2.5L (below 60)',
                        new: '₹4L (all ages) - Budget 2025',
                        available: true,
                      },
                      {
                        name: 'Rebate limit',
                        old: 'Up to ₹5L income',
                        new: 'Up to ₹12L income - Budget 2025',
                        available: true,
                      },
                    ],
                  },
                  {
                    category: 'Chapter VI-A deductions',
                    items: [
                      {
                        name: 'Section 80C (PPF, ELSS, etc.)',
                        old: 'Up to ₹1.5L',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Section 80CCD(1B) - Additional NPS',
                        old: 'Up to ₹50K',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Section 80CCD(2) - Employer NPS',
                        old: 'Available',
                        new: 'Available',
                        available: true,
                      },
                      {
                        name: 'Section 80D - Health insurance',
                        old: 'Up to ₹25K-₹1L',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Section 80E - Education loan interest',
                        old: 'Available',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Section 80G - Donations',
                        old: 'Available',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Section 80TTA/TTB - Savings interest',
                        old: '₹10K/₹50K',
                        new: 'Not available',
                        available: false,
                      },
                    ],
                  },
                  {
                    category: 'Property & loan benefits',
                    items: [
                      {
                        name: 'Home loan interest (24b)',
                        old: 'Up to ₹2L',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Section 80EEA - Affordable housing',
                        old: 'Up to ₹1.5L',
                        new: 'Not available',
                        available: false,
                      },
                    ],
                  },
                  {
                    category: 'Salary allowances',
                    items: [
                      {
                        name: 'House Rent Allowance (HRA)',
                        old: 'As per rules',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Leave Travel Allowance (LTA)',
                        old: 'As per rules',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Entertainment Allowance',
                        old: 'Available (Govt)',
                        new: 'Not available',
                        available: false,
                      },
                    ],
                  },
                  {
                    category: 'Special cases',
                    items: [
                      {
                        name: 'Section 80DD - Disability',
                        old: 'Available',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Section 80DDB - Medical treatment',
                        old: 'Available',
                        new: 'Not available',
                        available: false,
                      },
                      {
                        name: 'Family pension deduction',
                        old: 'Up to ₹15K',
                        new: 'Available',
                        available: true,
                      },
                      {
                        name: 'Professional tax',
                        old: 'Available',
                        new: 'Not available',
                        available: false,
                      },
                    ],
                  },
                ].map((category, categoryIndex) => (
                  <React.Fragment key={categoryIndex}>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={3}
                        className="py-2 sm:py-3 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm"
                      >
                        {category.category}
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <tr key={itemIndex} className="hover:bg-gray-50">
                        <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm">
                          {item.name}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-gray-700 text-xs sm:text-sm">
                          {item.old}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-center font-medium text-gray-700 text-xs sm:text-sm">
                          {item.new}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Decision Guide */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 mt-6 sm:mt-12">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
                Choose old regime if you:
              </h3>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-700">
                <li>
                  • Make significant tax-saving investments (₹1.5L in 80C)
                </li>
                <li>
                  • Have high home loan interest payments (up to ₹2L deduction)
                </li>
                <li>• Pay substantial house rent (HRA benefit available)</li>
                <li>
                  • Have health insurance premiums for family (80D deduction)
                </li>
                <li>• Want to maximize deductions for wealth building</li>
                <li>
                  • Have income above ₹15L where deductions provide better
                  savings
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
                Choose new regime if you: (Budget 2025 updated)
              </h3>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-700">
                <li>
                  •{' '}
                  <strong>
                    Have taxable income up to ₹12L (zero tax with new rebate)
                  </strong>
                </li>
                <li>• Don't make substantial tax-saving investments</li>
                <li>• Prefer simplified tax filing process</li>
                <li>• Are a young professional starting career</li>
                <li>• Want immediate tax relief without planning</li>
                <li>• Benefit from higher basic exemption (₹4L vs ₹2.5L)</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>
      {/* Tax Saving Options - Only show for old regime */}
      {taxRegime === 'old' && (
        <motion.section
          className="py-16"
          style={{ backgroundColor: COLORS.mist }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Tax saving investment options (Section 80C)
              </h2>
              <p className="text-xl" style={{ color: COLORS.smoke }}>
                Choose the best tax-saving investments for your portfolio
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {taxSavingOptions.map((option, index) => (
                <motion.div
                  key={option.name}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
                >
                  <div className="flex items-center mb-4">
                    <CheckCircle
                      size={20}
                      style={{ color: COLORS.sage }}
                      className="mr-3"
                    />
                    <h3 className="font-bold" style={{ color: COLORS.primary }}>
                      {option.name}
                    </h3>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Limit:</span>
                      <span
                        className="font-medium"
                        style={{ color: COLORS.charcoal }}
                      >
                        {option.limit}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Returns:</span>
                      <span
                        className="font-medium"
                        style={{ color: COLORS.sage }}
                      >
                        {option.returns}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lock-in:</span>
                      <span
                        className="font-medium"
                        style={{ color: COLORS.gold }}
                      >
                        {option.lockIn}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
      )}
      {/* Last Updated and Disclaimer */}
      <motion.section
        className="py-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.6 }}
      >
        <div className="mx-auto px-2">
          {/* Disclaimer */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-red-800 mb-4">
              Important disclaimer
            </h3>
            <div className="text-sm text-red-700 space-y-3">
              <p>
                This tax calculator is provided for informational and
                educational purposes only. While we strive to ensure accuracy
                and keep the calculator updated with the latest tax laws and
                Budget changes, the results may not be completely accurate or
                applicable to your specific financial situation.
              </p>
              <p>
                <strong>Please note:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  Tax calculations can be complex and depend on various factors
                  not covered by this calculator
                </li>
                <li>
                  Individual circumstances, additional income sources, and
                  specific deductions may affect your actual tax liability
                </li>
                <li>
                  Tax laws and regulations are subject to change and
                  interpretation
                </li>
                <li>
                  This calculator should not be used as the sole basis for tax
                  planning or filing decisions
                </li>
              </ul>
              <p>
                <strong>
                  We strongly recommend consulting with a qualified Chartered
                  Accountant (CA) or tax professional
                </strong>{' '}
                for personalized advice regarding your tax planning, compliance,
                and filing requirements. Professional guidance is essential for
                accurate tax calculations and optimal tax planning strategies.
              </p>
              <p>
                <strong>
                  {CONTACT_INFO.company.name} and its affiliates disclaim any
                  liability for decisions made based on the information provided
                  by this calculator.
                </strong>
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-md">
              <strong>Calculator logic last updated on:</strong> September 28,
              2025
            </p>
          </div>
        </div>
      </motion.section>
    </ToolLayout>
  );
};
